// Port of ios/Sources/ViewModels/ConnectionBanner.swift.
//
// WHETHER the conversation should say anything about the connection — and it usually should not.
//
// "I don't have that bad signal and it keeps appearing reconnecting" (user, 2026-09-17). The strip
// was drawn straight off the socket's raw state, so it appeared for every reconnect however brief:
// coming back to the app tears the socket down and builds a new one, and on a good link that whole
// round trip is a few hundred milliseconds. The banner was not reporting bad signal. It was
// reporting NORMAL LIFE, in a colour that looks like trouble.
//
// So a transient state has to EARN the strip by lasting. Below `graceMillis` nothing is said;
// past it the reader is told, because at that point something really is wrong and silence would
// be the other failure — a conversation that has quietly stopped receiving messages must not look
// like one where nobody is talking.
//
// A decision-for-decision twin of Android's `ConnectionBanner`, tests included.

/**
 * The socket's state, as Swift's `WebSocketConnectionState` enum (ios/Sources/Networking/
 * WebSocketManager.swift). Declared here structurally because the web socket manager lives in
 * the worker (src/data/net) and a core module must not import it; the manager's own state type
 * should be (or be assignable to) this one.
 */
export type WebSocketConnectionState =
  | { kind: 'disconnected' }
  | { kind: 'connecting' }
  | { kind: 'authenticating' }
  | { kind: 'connected' }
  | { kind: 'reconnecting'; attempt: number };

/**
 * - `none`: say nothing. Connected, or not yet worth mentioning.
 * - `connecting`: first connection of this session.
 * - `reconnecting`: a connection that existed and went away.
 */
export type ConnectionBannerKind = 'none' | 'connecting' | 'reconnecting';

export interface ConnectionBannerDecision {
  kind: ConnectionBannerKind;
  attempt: number;
}

/**
 * How long a connection attempt may run before the reader is told about it: comfortably
 * longer than a healthy reconnect, comfortably shorter than "why is nothing arriving?".
 */
const graceMillis = 2_500;

/**
 * From this attempt on, the strip carries the attempt NUMBER — a second failure is no longer
 * a blip, and the count is what tells a bad link apart from a server refusing.
 */
const attemptWorthShowing = 2;

function decision(kind: ConnectionBannerKind, attempt = 0): ConnectionBannerDecision {
  return { kind, attempt };
}

/** Whether the strip should name the attempt — see `attemptWorthShowing`. */
function showsAttempt(d: ConnectionBannerDecision): boolean {
  return d.kind === 'reconnecting' && d.attempt >= attemptWorthShowing;
}

/**
 * `millisInState`: how long the socket has been in `state`. Callers pass 0 to ask "should this
 * be said IMMEDIATELY?" and a large number (`Number.MAX_SAFE_INTEGER`) to ask "will it ever be
 * said?", which is how a view decides whether to bother waiting at all.
 */
function decide(state: WebSocketConnectionState, millisInState: number): ConnectionBannerDecision {
  switch (state.kind) {
    // Connected says nothing, and neither does disconnected: the socket is deliberately down
    // while the app is in the background or signed out, and a strip about that would be the
    // app complaining about its own decision.
    case 'connected':
    case 'disconnected':
      return decision('none');
    case 'connecting':
    case 'authenticating':
      return millisInState >= graceMillis ? decision('connecting') : decision('none');
    case 'reconnecting':
      // A second attempt is evidence rather than a blip: say it at once, and say which.
      if (state.attempt >= attemptWorthShowing) return decision('reconnecting', state.attempt);
      return millisInState >= graceMillis ? decision('reconnecting', state.attempt) : decision('none');
  }
}

export const ConnectionBanner = {
  graceMillis,
  attemptWorthShowing,
  showsAttempt,
  decide,
} as const;
